/**
 * Auth State Management Store
 *
 * Provides authentication state with JWT token management.
 * Implements ADR-001: Session & Token Storage
 *
 * CRITICAL SECURITY:
 * - Access tokens are stored in memory ONLY (not localStorage/sessionStorage)
 * - Tokens are lost on page close (by design - security feature)
 * - Refresh tokens are in HttpOnly cookies (handled by browser)
 *
 * Features:
 * - Zustand store for token state management
 * - React Context provider for route guard compatibility
 * - Token expiry tracking with 30s buffer
 * - isAuthenticated derived from token validity
 */

import { create } from "zustand";
import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";

// ============================================================================
// Types
// ============================================================================

export interface User {
  id: string;
  email: string;
  tenantId: string;
  role: "owner" | "admin" | "viewer";
  plan: "free" | "starter" | "pro" | "agency";
  emailVerified?: boolean;
  displayName?: string | null;
  avatarUrl?: string | null;
}

interface AuthStore {
  // Token state (in memory only - never persisted)
  accessToken: string | null;
  tokenExpiresAt: number | null;

  // User state
  user: User | null;

  // Loading state
  isLoading: boolean;

  // Actions
  setAccessToken: (token: string, expiresIn: number) => void;
  setUser: (user: User | null) => void;
  setLoading: (loading: boolean) => void;
  clearAuth: () => void;

  // Derived getters (not reactive - call when needed)
  getAccessToken: () => string | null;
  getIsAuthenticated: () => boolean;
}

// ============================================================================
// Zustand Store
// ============================================================================

/**
 * Zustand store for auth state management.
 * Tokens are stored in memory only - never persisted.
 */
export const useAuthStore = create<AuthStore>((set, get) => ({
  // Initial state
  accessToken: null,
  tokenExpiresAt: null,
  user: null,
  isLoading: true, // Start loading until initial auth check completes

  /**
   * Set access token with expiry time.
   * Token is stored in memory only.
   *
   * @param token - JWT access token
   * @param expiresIn - Expiration time in seconds
   */
  setAccessToken: (token: string, expiresIn: number) => {
    set({
      accessToken: token,
      tokenExpiresAt: Date.now() + expiresIn * 1000,
      isLoading: false,
    });
  },

  /**
   * Set user information (typically from token claims or API).
   */
  setUser: (user: User | null) => {
    set({ user });
  },

  /**
   * Set loading state.
   */
  setLoading: (loading: boolean) => {
    set({ isLoading: loading });
  },

  /**
   * Clear all auth state (logout).
   */
  clearAuth: () => {
    set({
      accessToken: null,
      tokenExpiresAt: null,
      user: null,
      isLoading: false,
    });
  },

  /**
   * Get access token if valid (not expired).
   * Includes 30s buffer for network latency.
   *
   * @returns Valid access token or null
   */
  getAccessToken: () => {
    const state = get();
    if (!state.accessToken || !state.tokenExpiresAt) {
      return null;
    }

    // Check if expired with 30s buffer
    const bufferMs = 30 * 1000;
    if (Date.now() > state.tokenExpiresAt - bufferMs) {
      return null;
    }

    return state.accessToken;
  },

  /**
   * Check if user is authenticated (has valid token).
   *
   * @returns true if authenticated
   */
  getIsAuthenticated: () => {
    return get().getAccessToken() !== null;
  },
}));

// Expose store on window for E2E test access (dev only)
if (import.meta.env.DEV) {
  (window as unknown as Record<string, unknown>).__authStore = useAuthStore;
}

// ============================================================================
// React Context (for route guard compatibility)
// ============================================================================

interface AuthState {
  isAuthenticated: boolean;
  isLoading: boolean;
  user: User | null;
  login: (user: User, token: string, expiresIn: number) => void;
  logout: () => void;
}

const AuthContext = createContext<AuthState | null>(null);

interface AuthProviderProps {
  children: ReactNode;
  initialUser?: User | null;
  initialLoading?: boolean;
}

/**
 * Auth Provider component.
 *
 * Wraps the app and provides auth context to route guards.
 * Syncs with Zustand store for token management.
 */
export function AuthProvider({
  children,
  initialUser = null,
  initialLoading,
}: AuthProviderProps) {
  // Get store actions and state
  const { setAccessToken, setUser, clearAuth, setLoading } = useAuthStore();

  // Subscribe to store changes for reactivity
  const accessToken = useAuthStore((state) => state.accessToken);
  const tokenExpiresAt = useAuthStore((state) => state.tokenExpiresAt);
  const user = useAuthStore((state) => state.user);
  const isLoading = useAuthStore((state) => state.isLoading);

  // Derive isAuthenticated synchronously from token validity.
  // Using useMemo (not useEffect + useState) eliminates a one-render lag
  // that caused _authenticated.beforeLoad to see stale isAuthenticated=false
  // when navigating immediately after login.
  const bufferMs = 30 * 1000;
  const isAuthenticated = useMemo(
    () => !!accessToken && !!tokenExpiresAt && Date.now() < tokenExpiresAt - bufferMs,
    [accessToken, tokenExpiresAt, bufferMs],
  );

  // Force re-render when token expires (edge case: user stays on page until expiry)
  const [, setExpired] = useState(false);
  useEffect(() => {
    if (!accessToken || !tokenExpiresAt) return undefined;

    const timeUntilExpiry = tokenExpiresAt - bufferMs - Date.now();
    if (timeUntilExpiry <= 0) return undefined;

    const timer = setTimeout(() => setExpired((v) => !v), timeUntilExpiry);
    return () => clearTimeout(timer);
  }, [accessToken, tokenExpiresAt, bufferMs]);

  // Initialize with props (used for testing only)
  useEffect(() => {
    if (initialUser) {
      setUser(initialUser);
    }
    // Only override loading when explicitly passed by tests.
    // Default isLoading=true is set in the Zustand store initializer;
    // useAuthInit manages the loading lifecycle at runtime.
    if (initialLoading !== undefined) {
      setLoading(initialLoading);
    }
  }, [initialUser, initialLoading, setUser, setLoading]);

  const login = (newUser: User, token: string, expiresIn: number) => {
    setAccessToken(token, expiresIn);
    setUser(newUser);
  };

  const logout = () => {
    clearAuth();
  };

  return (
    <AuthContext.Provider
      value={{
        isAuthenticated,
        isLoading,
        user,
        login,
        logout,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

/**
 * Hook to access auth state in components.
 *
 * @returns Auth state with login/logout methods
 * @throws Error if used outside AuthProvider
 */
export function useAuth(): AuthState {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return context;
}

// Export context for testing purposes
export { AuthContext };
